import {
  FormFieldBlueprint,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  AppRootElementBlueprint,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';

import { AzureTenantUserPicker } from './components/AzurePicker/AzurePicker';
import {
  GitHubOrgNamePicker,
  validateOrgName,
} from './components/GitHubOrgNamePicker/GitHubOrgNamePicker';
import { DisclaimerPicker } from './components/DisclaimerPicker/DisclaimerPicker';
import { LeanixPicker } from './components/LeanixPicker/LeanixPicker';
import { AzureGroupMemberPicker } from './components/AzureGroupMemberPicker/AzureGroupMemberPicker';
import { MicrosoftLoginPicker } from './components/MicrosoftLoginPicker/MicrosoftLoginPicker';
import { AzureCloudPicker } from './components/Megatron1Jumphost/AzureCloudPicker.tsx';
import { JumphostDetails } from './components/Megatron1Jumphost/JumphostDetailsExtension.tsx';
import { EmailStatusPicker } from './components/EmailStatusPicker/EmailStatusPicker';
import { AuthTokenResolver } from './components/CloudIdpAuthTokenPicker/AuthTokenResolver.tsx';
import { TemplateSupportButton } from './components/TemplateSupportButton/TemplateSupportButton';

// The `name` passed to `createFormField` is the `ui:field` value templates use.

const azureTenantUserPickerField = FormFieldBlueprint.make({
  name: 'azure-tenant-user-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'AzureTenantUserPicker',
        component: AzureTenantUserPicker,
        validation: (_value, validation, context) => {
          const list = Object.values(context?.formData || {}).find(
            Array.isArray,
          );
          if (!Array.isArray(list)) return;
          const ids = list
            .map((v: any) =>
              typeof v === 'string' ? v : v?.mail || v?.userName,
            )
            .filter(Boolean);
          if (new Set(ids).size !== ids.length) {
            validation.addError('Duplicate users are not allowed');
          }
        },
      }),
  },
});

const gitHubOrgNamePickerField = FormFieldBlueprint.make({
  name: 'git-hub-org-name-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'GitHubOrgNamePicker',
        component: GitHubOrgNamePicker,
        validation: (value, validation) => {
          if (!value) {
            validation.addError('Organization name is required');
            return;
          }
          const result = validateOrgName(value);
          if (!result.isValid) {
            validation.addError(result.message);
          }
        },
      }),
  },
});

const disclaimerPickerField = FormFieldBlueprint.make({
  name: 'disclaimer-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'DisclaimerPicker',
        component: DisclaimerPicker,
        validation: (value, validation) => {
          if (!value) {
            validation.addError('You must accept the disclaimer to proceed');
          }
        },
      }),
  },
});

const leanixPickerField = FormFieldBlueprint.make({
  name: 'leanix-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'LeanixPicker',
        component: LeanixPicker,
      }),
  },
});

const azureGroupMemberPickerField = FormFieldBlueprint.make({
  name: 'azure-group-member-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'AzureGroupMemberPicker',
        component: AzureGroupMemberPicker,
        validation: (_value, validation, context) => {
          const owners = context?.formData?.owners;
          if (!Array.isArray(owners)) return;
          const identifiers = owners
            .map((item: any) =>
              typeof item === 'string' ? item : item?.mail || item?.userName,
            )
            .filter(Boolean);
          if (new Set(identifiers).size !== identifiers.length) {
            validation.addError('Duplicate owners are not allowed');
          }
        },
      }),
  },
});

const microsoftLoginPickerField = FormFieldBlueprint.make({
  name: 'microsoft-login-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'MicrosoftLoginPicker',
        component: MicrosoftLoginPicker,
        validation: (value, validation) => {
          if (!value) {
            validation.addError('Azure Entra authentication is required');
          }
        },
      }),
  },
});

const jumphostDetailsField = FormFieldBlueprint.make({
  name: 'jumphost-details',
  params: {
    field: async () =>
      createFormField({
        name: 'JumphostDetails',
        component: JumphostDetails,
      }),
  },
});

const azureCloudPickerField = FormFieldBlueprint.make({
  name: 'azure-cloud-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'AzureCloudPicker',
        component: AzureCloudPicker,
      }),
  },
});

const emailStatusPickerField = FormFieldBlueprint.make({
  name: 'email-status-picker',
  params: {
    field: async () =>
      createFormField({
        name: 'EmailStatusPicker',
        component: EmailStatusPicker,
        validation: (value, validation) => {
          if (!value) {
            validation.addError(
              'A valid and eligible email address is required',
            );
          }
        },
      }),
  },
});

const authTokenResolverField = FormFieldBlueprint.make({
  name: 'auth-token-resolver',
  params: {
    field: async () =>
      createFormField({
        name: 'AuthTokenResolver',
        component: AuthTokenResolver,
      }),
  },
});

/**
 * Floating support button for the template currently open under
 * `/create/templates/:namespace/:name`. Renders nothing elsewhere.
 */
const templateSupportButton = AppRootElementBlueprint.make({
  name: 'template-support-button',
  params: {
    element: <TemplateSupportButton />,
  },
});

/**
 * Custom scaffolder form fields (`ui:field` names) and the template support
 * button. The fields attach to `api:scaffolder/form-fields`.
 *
 * @alpha
 */
export const scaffolderCustomFieldExtensionsPlugin = createFrontendPlugin({
  pluginId: 'scaffolder-custom-field-extensions',
  extensions: [
    azureTenantUserPickerField,
    gitHubOrgNamePickerField,
    disclaimerPickerField,
    leanixPickerField,
    azureGroupMemberPickerField,
    microsoftLoginPickerField,
    jumphostDetailsField,
    azureCloudPickerField,
    emailStatusPickerField,
    authTokenResolverField,
    templateSupportButton,
  ],
});
